import useSWR from "swr";
import useUser from "../../context/userHook";
import { getAccountSettings, updateSetting } from "../../../utils/requests/accountSettingsRequest";
import useTokenHook from "../../context/useTokenHook";
import useMondayContext from "../../context/useMondayContext";
import logger from "../../../lib/logger";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import mondaySdk from "monday-sdk-js";
import {
  buildReauthorizeUrl,
  getReauthorizationRefusal,
  rememberPendingSetting,
} from "../../../utils/requests/reauthorization";

const monday = mondaySdk();

const humanize = (settingKey: string) =>
  settingKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export interface UpdateAccountSettingOptions {
  /**
   * The call is the automatic retry right after the user came back from monday's consent
   * screen. A second refusal then means the grant still lacks the scope (the user declined or
   * the app version lacks it), so report it instead of redirecting again.
   */
  afterReauthorization?: boolean;
}

const useAccountSettings = () => {
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();
  const { appVersionId } = useMondayContext();
  const navigate = useNavigate();
  const [updateSettingLoading, setUpdateSettingLoading] = useState(false);
  const [updateSettingError, setUpdateSettingError] = useState<Error | null>(null);

  const { error, data, isLoading, mutate, isValidating } = useSWR(token && userId ? `account-settings:${userId}` : null, () =>
    getAccountSettings(token!, String(accountId))
  );

  const updateAccountSetting = async (settingKey: string, value: boolean, options: UpdateAccountSettingOptions = {}) => {
    setUpdateSettingLoading(true);
    setUpdateSettingError(null);
    try {
      await updateSetting(token!, String(accountId), settingKey, value);
      monday.execute("notice", {
        message: `${humanize(settingKey)} has been ${value ? "enabled" : "disabled"}`,
        type: "success",
        timeout: 5000,
      });
    } catch (err: any) {
      setUpdateSettingError(err);

      // The backend refuses to turn on a setting whose feature needs a monday permission the
      // user's grant does not carry yet. New scopes require an account admin to approve the
      // app update in monday (Administration > Apps > Same Item Multiple Boards > Update).
      // Re-running the OAuth flow does NOT grant missing scopes, so we guide users to the
      // admin approval flow instead of sending them through re-authorization.
      const refusal = getReauthorizationRefusal(err);
      if (refusal && (refusal.requires_admin_approval === true || refusal.reason === "missing_scope")) {
        logger.log(`Missing scope for ${settingKey}:`, { ...refusal });
        // Use the backend's error_message if available, otherwise use our default.
        const message = refusal.error_message || 
          `${humanize(settingKey)} needs a new permission. Ask an admin on your monday account to open Administration > Apps > Same Item Multiple Boards, click Update, and accept the permissions.`;
        monday.execute("notice", {
          message,
          type: "info",
          timeout: 10000,
        });
        throw err;
      }

      // Handle actual auth failures (expired/revoked tokens) separately.
      if (refusal && refusal.reason === "token_invalid") {
        if (options.afterReauthorization) {
          logger.error(`Re-authorization did not restore access for ${settingKey}:`, { ...refusal });
          monday.execute("notice", {
            message: `${humanize(settingKey)} stays off. Please try authorizing again.`,
            type: "error",
            timeout: 10000,
          });
        } else {
          logger.log(`Re-authorization required to enable ${settingKey}:`, { ...refusal });
          monday.execute("notice", {
            message: `${humanize(settingKey)} needs authorization. Taking you to monday to authorize…`,
            type: "info",
            timeout: 8000,
          });
          rememberPendingSetting(settingKey);
          navigate("/onboarding/mondayOAuth", {
            state: { authUrl: buildReauthorizeUrl({ appVersionId, returnTo: "settings" }) },
          });
        }
        throw err;
      }

      logger.error(`Error updating ${settingKey} setting:`, err);
      monday.execute("notice", {
        message: `Failed to update ${humanize(settingKey)}`,
        type: "error",
        timeout: 5000,
      });
      throw err;
    } finally {
      setUpdateSettingLoading(false);
    }
  };

  return {
    isLoading,
    error,
    accountSettingsData: data || null,
    mutate,
    isValidating,
    updateAccountSetting,
    updateSettingLoading,
    updateSettingError,
  };
};

export default useAccountSettings;
